import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Modal,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@material-ui/core';
import BuzzTable from '../../../components/BuzzTable/BuzzTable';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import CachedIcon from '@material-ui/icons/Cached';
import { useDispatch } from 'react-redux';
import { useSelector } from 'react-redux';
import useToast from '../../../hooks/useToast';
import useLoader from '../../../hooks/useLoader';
import CustomForm from '../../../components/FormRenderer/CustomForm';
import {
  initializeFormData,
  sanitizePayload,
} from '../../../components/FormRenderer/utils';
import {
  createCurriculumFramework,
  getAllCurriculumFrameworks,
  rebuildCurriculumFrameworkCaches,
  updateCurriculumFramework,
} from '../AkademySlice';

// `levels` is the framework's single source of truth for level names — a node's own
// `levelName` is only a cache of it. Renaming a level here cascades to every node at that
// level and to every descendant's `ancestorPath`, so the depth of a framework is a
// property of the framework, not something a node picker infers.
const describeLevels = (levels) =>
  [...(levels || [])]
    .sort((a, b) => a.level - b.level)
    .map(({ level, name }) => `${level}. ${name}`)
    .join('  →  ');

// The backend refuses a BCC create that carries neither a `schoolId` nor `scope: GLOBAL`
// (FRAMEWORK_SCOPE_REQUIRED): "no school" used to silently mean Global, which leaked
// schools' private drafts to every tenant. So Global is a value the admin picks, never the
// default of an untouched field.
const GLOBAL_SCOPE = 'GLOBAL';

const buildCreatePayload = ({ schoolId, ...rest }) =>
  sanitizePayload(
    schoolId === GLOBAL_SCOPE
      ? { ...rest, scope: GLOBAL_SCOPE }
      : { ...rest, schoolId }
  );

function AkademyCurriculumFrameworks() {
  const dispatch = useDispatch();
  const { ErrorToast, SuccessToast } = useToast();
  const { handleLoader } = useLoader();

  const [showFormModal, setFormModal] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});

  const { curriculumFrameworks, schools } = useSelector(
    ({ akademyReducer }) => {
      return {
        schools: akademyReducer.schools,
        curriculumFrameworks: akademyReducer.curriculumFrameworks,
      };
    }
  );

  useEffect(() => {
    dispatch(getAllCurriculumFrameworks());
  }, []);

  // `schoolId` is create-only: a framework never moves between Global and SchoolDefined
  // after creation, so `UpdateCurriculumFrameworkDTO` has no such field and sending one
  // would be silently dropped by the backend's validation whitelist.
  const buildFormTemplate = (mode) => {
    const levelsField = {
      id: 'form.levels',
      title: 'Levels (1 = top of the tree, up to 5)',
      slug: 'levels',
      type: 'Array',
      // A level already saved on this framework keeps its number fixed — renumbering an
      // in-use level would desync every node's cached `level`/`ancestorPath` until the
      // caches are rebuilt. Newly added levels (this edit session only) are unaffected.
      disableExistingSlugs: ['level'],
      arrayElementType: [
        {
          id: 'form.levels.level',
          title: 'Level',
          slug: 'level',
          type: 'Number',
          required: true,
        },
        {
          id: 'form.levels.name',
          title: 'Level Name',
          slug: 'name',
          type: 'String',
          required: true,
        },
      ],
    };

    const template = [
      {
        id: 'form.code',
        title: 'Code (unique acronym, e.g. EYFS)',
        slug: 'code',
        type: 'String',
        required: true,
      },
      {
        id: 'form.name',
        title: 'Framework Name',
        slug: 'name',
        type: 'String',
        required: true,
      },
      levelsField,
    ];

    if (mode === 'CREATE')
      template.push({
        id: 'form.school',
        title: 'School (or Global, visible to every school)',
        slug: 'schoolId',
        type: 'Select',
        required: true,
        options: [
          { label: 'Global — Beanstalk standard', value: GLOBAL_SCOPE },
          ...(schools || []).map((school) => {
            return { label: school.name, value: school._id };
          }),
        ],
      });

    return template;
  };

  const frameworkColumns = [
    // { Header: 'Framework Id', accessor: '_id' },
    { Header: 'Code', accessor: 'code' },
    { Header: 'Name', accessor: 'name' },
    {
      Header: 'Levels',
      Cell: (val) => <>{describeLevels(val.cell.row.original.levels)}</>,
    },
    {
      Header: 'Scope',
      Cell: (val) => {
        const { schoolId } = val.cell.row.original;
        if (!schoolId) return <>Global</>;

        const school = (schools || []).find(({ _id }) => _id === schoolId);
        return <>{school ? school.name : schoolId}</>;
      },
    },
    {
      Header: 'Action',
      Cell: (val) => {
        const { _id } = val.cell.row.original;
        const [anchorEl, setAnchorEl] = useState(null);
        const open = Boolean(anchorEl);

        const handleMenuOpen = (event) => setAnchorEl(event.currentTarget);
        const handleMenuClose = () => setAnchorEl(null);

        return (
          <>
            <IconButton
              aria-label="more"
              aria-controls="framework-actions-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              id="framework-actions-menu"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleMenuClose}>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleEdit(_id);
                }}>
                <ListItemIcon>
                  <EditIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>Edit</ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  handleRebuildCaches(_id);
                }}>
                <ListItemIcon>
                  <CachedIcon color="secondary" fontSize="small" />
                </ListItemIcon>
                <ListItemText>Rebuild derived node caches</ListItemText>
              </MenuItem>
              <MenuItem disabled>
                <ListItemIcon>
                  <DeleteIcon fontSize="small" style={{ color: 'grey' }} />
                </ListItemIcon>
                <ListItemText>Delete</ListItemText>
              </MenuItem>
            </Menu>
          </>
        );
      },
    },
  ];

  const openFormModal = () => setFormModal(true);
  const closeFormModal = () => setFormModal(false);

  const handleEdit = (id) => {
    setSelectedRowId(id);
    const { code, name, levels } = curriculumFrameworks.filter(
      ({ _id }) => _id === id
    )[0];
    setFormTemplate(buildFormTemplate('EDIT'));
    // Cloned rather than passed straight through: `levels` is a reference into Redux
    // state, which Immer freezes in development — editing a level in the form later would
    // throw trying to mutate a frozen object.
    setFormData({
      _id: id,
      code,
      name,
      levels: (levels || []).map((level) => ({ ...level })),
    });
    openFormModal();
  };

  const handleCreateNew = () => {
    setSelectedRowId(null);
    const template = buildFormTemplate('CREATE');
    setFormTemplate(template);
    setFormData(initializeFormData(template));
    openFormModal();
  };

  const handleRebuildCaches = async (id) => {
    if (
      !window.confirm(
        'Recompute frameworkCode, levelName, ancestors and ancestorPath on every node in this framework?'
      )
    )
      return;

    handleLoader(true);
    dispatch(rebuildCurriculumFrameworkCaches({ _id: id }))
      .unwrap()
      .then(({ totalNodes, updated, skipped }) => {
        SuccessToast(
          `Rebuilt ${updated} of ${totalNodes} nodes (${skipped} already correct)`
        );
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  const handleSave = async () => {
    if (selectedRowId === null && !formData.schoolId) {
      ErrorToast('Choose a school, or Global for a Beanstalk standard.');
      return;
    }

    handleLoader(true);

    dispatch(
      selectedRowId === null
        ? createCurriculumFramework(buildCreatePayload(formData))
        : updateCurriculumFramework(formData)
    )
      .unwrap()
      .then(() => {
        SuccessToast(
          selectedRowId === null ? 'Framework created' : 'Framework saved'
        );
        dispatch(getAllCurriculumFrameworks());
        closeFormModal();
      })
      .catch((error) => {
        console.error('rejected', error);
        ErrorToast(error.message);
      });

    handleLoader(false);
  };

  return (
    <Box>
      <Button onClick={handleCreateNew} variant={'contained'} color="primary">
        Add Framework
      </Button>
      <Box my={2}>
        <BuzzTable
          columns={frameworkColumns}
          data={curriculumFrameworks}
          isHandlePagination={true}
        />
      </Box>
      <Modal open={showFormModal} onClose={closeFormModal}>
        <CustomForm
          formFor={'Framework'}
          formTemplate={formTemplate}
          formData={formData}
          setFormData={(slug, newValue) => {
            setFormData((state) => {
              state[slug] = newValue;
              return state;
            });
            setFormTemplate((state) => {
              return JSON.parse(JSON.stringify(state));
            });
          }}
          mode={selectedRowId === null ? 'CREATE' : 'EDIT'}
          handleSave={handleSave}
        />
      </Modal>
    </Box>
  );
}

export default AkademyCurriculumFrameworks;
